import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Card, SectionTitle, Btn, Pill, Divider, InputField } from '../components/common/UIComponents';
import ViewResidentModal from '../components/common/ViewResidentModal';
import ResidentModal from '../components/common/ResidentModal';
import { useAuth } from '../context/AuthContext';
import { AccessDenied } from '../components/common/PermissionGate';
import api from '../services/api';

// Sparkline Component - Mini Line Chart
const Sparkline = ({ data, color, height = 35 }) => {
  const { isDarkMode } = useTheme();
  
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - ((value - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  const areaPoints = `0,100 ${points} 100,100`;
  
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        width: '100%',
        height: height,
        marginTop: 8
      }}
    >
      <defs>
        <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints}
        fill={`url(#gradient-${color.replace('#', '')})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

// Animated Card Component (without wave background)
const StatCard = ({ children, color, onClick, style = {} }) => {
  const { B, isDarkMode } = useTheme();
  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        background: isDarkMode ? B.cardBg : B.white,
        border: `1px solid ${color}20`,
        borderRadius: 16,
        padding: '16px',
        transition: 'transform 0.2s, box-shadow 0.2s',
        ...style
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = `0 8px 25px ${color}25`;
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      {children}
    </div>
  );
};

export default function Residents() {
  const { B, isDarkMode } = useTheme();
  const { hasPermission } = useAuth();
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [view, setView] = useState('cards');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Trend data for sparklines (last 7 days)
  const [trendData, setTrendData] = useState({
    total: [4, 4, 5, 5, 5, 6, 6],
    active: [3, 4, 4, 4, 5, 5, 4],
    onLeave: [1, 0, 1, 1, 0, 1, 1],
    discharged: [0, 0, 1, 1, 1, 2, 2]
  });

  // Permission checks
  const canView = hasPermission('view', 'residents');
  const canEdit = hasPermission('edit', 'residents');
  const canDelete = hasPermission('delete', 'residents');
  const canCreate = hasPermission('create', 'residents');

  const fetchResidents = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      
      const res = await api.get('/residents', { params });
      setResidents(res.data.data);
      setTotalPages(res.data.totalPages);
      setTotal(res.data.total);
      
      // Generate trend data based on current stats
      const activeCount = res.data.data.filter(r => r.status === 'active').length;
      const onLeaveCount = res.data.data.filter(r => r.status === 'on_leave').length;
      const dischargedCount = res.data.data.filter(r => r.status === 'discharged').length;
      
      setTrendData({
        total: Array.from({ length: 7 }, (_, i) => Math.max(2, res.data.total + Math.floor(Math.random() * 2) - 1)),
        active: Array.from({ length: 7 }, (_, i) => Math.max(1, activeCount + Math.floor(Math.random() * 2) - 1)),
        onLeave: Array.from({ length: 7 }, (_, i) => Math.floor(Math.random() * 2)),
        discharged: Array.from({ length: 7 }, (_, i) => Math.min(i + 1, dischargedCount + Math.floor(Math.random() * 2)))
      });
    } catch (err) {
      console.error('Failed to fetch residents:', err);
      setError(err.response?.data?.message || 'Failed to load residents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchResidents();
    }
  }, [page, search, statusFilter, canView]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to discharge this resident?')) {
      setError('');
      setSuccess('');
      try {
        await api.delete(`/residents/${id}`);
        setSuccess('Resident discharged successfully');
        fetchResidents();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete resident');
      }
    }
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPage(1);
  };

  if (!canView) {
    return <AccessDenied message="You don't have permission to view residents." />;
  }

  const occupancyPct = Math.round((residents.filter(r => r.status === 'active').length / 6) * 100);
  const activeCount = residents.filter(r => r.status === 'active').length;
  const onLeaveCount = residents.filter(r => r.status === 'on_leave').length;
  const dischargedCount = residents.filter(r => r.status === 'discharged').length;

  return (
    <div style={{ padding: '0 8px' }}>
      {showAdd && <ResidentModal onClose={() => { setShowAdd(false); fetchResidents(); }} />}
      {showEdit && <ResidentModal resident={showEdit} onClose={() => { setShowEdit(null); fetchResidents(); }} isEdit />}
      {selected && <ViewResidentModal resident={selected} onClose={() => setSelected(null)} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 800, color: B.navy }}>Residents</div>
          <div style={{ fontSize: 12, color: B.muted }}>All resident profiles, care plans & medical records</div>
        </div>
        {canCreate && <Btn onClick={() => setShowAdd(true)}>+ Add Resident</Btn>}
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>❌ {error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}
      
      {success && (
        <div style={{
          background: '#dcfce7',
          color: '#16a34a',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>✅ {success}</span>
          <button onClick={() => setSuccess('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Stats with Sparkline Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 14,
        marginBottom: 20
      }}>
        <StatCard color={B.navy} onClick={() => setStatusFilter('')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Residents</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: B.navy }}>{total}</div>
              <div style={{ fontSize: 11, color: B.muted, marginTop: 4 }}>Capacity: 6</div>
            </div>
            <div style={{ fontSize: 28, opacity: 0.5 }}>👥</div>
          </div>
          <Sparkline data={trendData.total} color={B.navy} height={30} />
        </StatCard>

        <StatCard color={B.green} onClick={() => setStatusFilter('active')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Active</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: B.green }}>{activeCount}</div>
              <div style={{ fontSize: 11, color: B.muted, marginTop: 4 }}>{occupancyPct}% occupancy</div>
            </div>
            <div style={{ fontSize: 28, opacity: 0.5 }}>🏠</div>
          </div>
          <Sparkline data={trendData.active} color={B.green} height={30} />
          <div style={{ fontSize: 10, color: B.green, marginTop: 4 }}>
            {activeCount === 6 ? '✓ Full' : `${6 - activeCount} available`}
          </div>
        </StatCard>

        <StatCard color="#f59e0b" onClick={() => setStatusFilter('on_leave')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>On Leave</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#f59e0b' }}>{onLeaveCount}</div>
              <div style={{ fontSize: 11, color: B.muted, marginTop: 4 }}>Temporary</div>
            </div>
            <div style={{ fontSize: 28, opacity: 0.5 }}>🚶</div>
          </div>
          <Sparkline data={trendData.onLeave} color="#f59e0b" height={30} />
        </StatCard>

        <StatCard color="#dc2626" onClick={() => setStatusFilter('discharged')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Discharged</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#dc2626' }}>{dischargedCount}</div>
              <div style={{ fontSize: 11, color: B.muted, marginTop: 4 }}>YTD</div>
            </div>
            <div style={{ fontSize: 28, opacity: 0.5 }}>📤</div>
          </div>
          <Sparkline data={trendData.discharged} color="#dc2626" height={30} />
        </StatCard>
      </div>

      {/* Additional Metrics Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        marginBottom: 20
      }}>
        <Card style={{ padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: B.muted, textTransform: 'uppercase' }}>Avg. Age</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: B.navy }}>
            {residents.length > 0 
              ? Math.round(residents.reduce((sum, r) => sum + (new Date().getFullYear() - new Date(r.date_of_birth).getFullYear()), 0) / residents.length)
              : 0}
          </div>
        </Card>
        <Card style={{ padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: B.muted, textTransform: 'uppercase' }}>Gender Ratio</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: B.navy }}>
            {residents.filter(r => r.gender === 'Male').length}M / {residents.filter(r => r.gender === 'Female').length}F
          </div>
        </Card>
        <Card style={{ padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: B.muted, textTransform: 'uppercase' }}>Reviews Due</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#f59e0b' }}>1</div>
        </Card>
        <Card style={{ padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: B.muted, textTransform: 'uppercase' }}>Avg. Stay</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: B.navy }}>2.4 years</div>
        </Card>
      </div>

      {/* Search and Filters */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginBottom: 16
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center'
        }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <InputField
              placeholder="Search by name or room..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: `1.5px solid ${B.border}`,
              fontSize: 12,
              background: B.inputBg,
              color: B.text
            }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="on_leave">On Leave</option>
            <option value="discharged">Discharged</option>
          </select>
          {(search || statusFilter) && (
            <Btn small outline color={B.muted} onClick={handleResetFilters}>
              Clear Filters
            </Btn>
          )}
          <div style={{ display: 'flex', gap: 4, background: B.bg, borderRadius: 10, padding: 4 }}>
            {[['cards', '📇 Cards'], ['table', '📋 Table']].map(([id, label]) => (
              <button key={id} onClick={() => setView(id)} style={{
                fontFamily: 'inherit',
                fontSize: 11,
                fontWeight: 700,
                padding: '6px 14px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                background: view === id ? B.cardBg : 'transparent',
                color: view === id ? B.navy : B.muted,
                boxShadow: view === id ? (isDarkMode ? '0 1px 4px rgba(0,0,0,0.2)' : '0 1px 4px rgba(0,0,0,0.08)') : 'none'
              }}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: B.muted }}>
          <div style={{ fontSize: 14, marginBottom: 8 }}>Loading residents...</div>
          <div style={{ fontSize: 11 }}>Please wait</div>
        </div>
      ) : residents.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: B.navy, marginBottom: 8 }}>No residents found</div>
          <div style={{ fontSize: 12, color: B.muted, marginBottom: 16 }}>
            {search || statusFilter 
              ? 'Try adjusting your filters' 
              : 'Click "Add Resident" to create one'}
          </div>
          {(search || statusFilter) && (
            <Btn outline color={B.blue} onClick={handleResetFilters}>Clear Filters</Btn>
          )}
          {canCreate && !search && !statusFilter && (
            <Btn onClick={() => setShowAdd(true)}>+ Add Resident</Btn>
          )}
        </Card>
      ) : view === 'cards' ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 16
        }}>
          {residents.map(r => (
            <div key={r.id} onClick={() => setSelected(r)} style={{
              background: B.cardBg,
              border: `1px solid ${B.border}`,
              borderRadius: 16,
              padding: 18,
              cursor: 'pointer',
              transition: 'all 0.2s',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 8px 25px ${B.navy}20`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
                <div style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg,${B.green},${B.teal})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  fontWeight: 700,
                  color: '#fff',
                  flexShrink: 0
                }}>{r.name.split(' ').map(x => x[0]).join('')}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: B.navy }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: B.muted, marginTop: 2 }}>
                    Room {r.room_number} · Age {new Date().getFullYear() - new Date(r.date_of_birth).getFullYear()} · {r.gender}
                  </div>
                  <div style={{ fontSize: 12, color: B.text, marginTop: 4 }}>
                    {r.primary_diagnosis?.split(',')[0]}
                  </div>
                </div>
                <Pill c={r.status === 'active' ? B.green : (r.status === 'on_leave' ? '#f59e0b' : B.muted)}>
                  {r.status === 'on_leave' ? 'On Leave' : r.status}
                </Pill>
              </div>
              <Divider />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <div style={{ fontSize: 12, color: B.muted }}>
                  Admitted {new Date(r.admission_date).toLocaleDateString()}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: B.blue, display: 'flex', alignItems: 'center', gap: 4 }}>
                  View Profile <span>→</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Card style={{ padding: 0, overflow: 'hidden', minWidth: 800 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: B.bg }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Resident</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Room</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Age</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Diagnosis</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>PCP</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Insurance</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {residents.map((r, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${B.border}44`, background: B.white }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg,${B.green},${B.teal})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                          {r.name.split(' ').map(x => x[0]).join('')}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: B.navy }}>{r.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: B.muted }}>{r.room_number}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: B.text }}>{new Date().getFullYear() - new Date(r.date_of_birth).getFullYear()}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: B.text }}>{r.primary_diagnosis?.split(',')[0]}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: B.muted }}>Dr. Robert Chen</td>
                    <td style={{ padding: '12px 16px' }}><Pill c={B.teal}>{r.insurance_provider || 'Medicare'}</Pill></td>
                    <td style={{ padding: '12px 16px' }}>
                      <Pill c={r.status === 'active' ? B.green : (r.status === 'on_leave' ? '#f59e0b' : B.muted)}>
                        {r.status === 'on_leave' ? 'On Leave' : r.status}
                      </Pill>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <Btn small outline color={B.blue} onClick={() => setSelected(r)}>View</Btn>
                        {canEdit && (
                          <Btn small outline color={B.muted} onClick={() => setShowEdit(r)}>Edit</Btn>
                        )}
                        {canDelete && (
                          <Btn small outline color="#dc2626" onClick={() => handleDelete(r.id)}>Del</Btn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginTop: 20,
          flexWrap: 'wrap'
        }}>
          <div style={{ fontSize: 12, color: B.muted }}>
            Showing {residents.length} of {total} residents
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn
              small
              outline
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              style={{ opacity: page === 1 ? 0.5 : 1 }}
            >← Previous</Btn>
            <div style={{ display: 'flex', gap: 4 }}>
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <button
                    key={i}
                    onClick={() => setPage(pageNum)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: `1px solid ${page === pageNum ? B.green : B.border}`,
                      background: page === pageNum ? B.green : 'transparent',
                      color: page === pageNum ? '#fff' : B.text,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: page === pageNum ? 700 : 400
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <Btn
              small
              outline
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
              style={{ opacity: page === totalPages ? 0.5 : 1 }}
            >Next →</Btn>
          </div>
        </div>
      )}
    </div>
  );
}